import { useCallback, useMemo, useReducer, useRef } from 'react';
import { normalizeCogniveId } from '../components/optimizer/optimizerIdentityHelpers';
import { loadedFactorSlice } from '../utils/loadedFactorSlice';

const EMPTY_METRICS = Object.freeze({
    loadedFactorContext: null,
    factorSliceOwner: null,
    riskMetricsActPort: null,
    riskMetricsOptPort: null,
    riskMetricsDeltaPort: null,
    attributeRiskMetrics: null,
    constraintEvaluation: null,
    resultIssuedAt: null,
    solverStatus: null,
    displayedExpectedReturnPolicy: null,
    risksByFactor: null,
});
const EMPTY_OVERLAY = Object.freeze({
    optimizedGmvsMap: null,
    receiptBaselineGmvsMap: null,
    receiptDisplaySource: null,
    displayedResultIdentity: null,
    receiptInputRows: null,
    receiptPosSizingContextDigest: null,
    showPortfolioMetrics: false,
});

export function createOptimizerResultState() {
    return {
        ...EMPTY_METRICS,
        ...EMPTY_OVERLAY,
        risksByFactor: [],
        constraintSummary: {},
        completedOptimizerRun: null,
    };
}

function receiptBaselineGmvs(securityRows) {
    if (!Array.isArray(securityRows)) return null;
    return Object.fromEntries(securityRows.map((row) => [
        normalizeCogniveId(row?.cognive_id),
        row?.gmv_actual,
    ]).filter(([cogniveId, gmv]) => (
        cogniveId && typeof gmv === 'number' && Number.isFinite(gmv)
    )));
}

function runProjection(response, owner) {
    const result = response.result;
    const riskMetrics = result.risk_metrics;
    return {
        loadedFactorContext: loadedFactorSlice(response.factor_context),
        factorSliceOwner: owner,
        riskMetricsActPort: riskMetrics.act_port,
        riskMetricsOptPort: riskMetrics.opt_port,
        riskMetricsDeltaPort: riskMetrics.delta_port,
        risksByFactor: result.risks_by_factor,
        attributeRiskMetrics: response.attributeRiskMetrics,
        constraintEvaluation: response.constraintEvaluation ?? null,
        resultIssuedAt: response.receipt.issued_at ?? null,
        solverStatus: response.solver_status ?? null,
        displayedExpectedReturnPolicy: response.expected_return_policy ?? null,
        constraintSummary: result.constraint_summary ?? {},
    };
}

function savedProjection(detail, owner) {
    const riskMetrics = detail.result.risk_metrics;
    return {
        loadedFactorContext: loadedFactorSlice(detail.factor_context ?? detail.portfolio),
        factorSliceOwner: owner,
        riskMetricsActPort: riskMetrics?.act_port ?? null,
        riskMetricsOptPort: riskMetrics?.opt_port ?? null,
        riskMetricsDeltaPort: riskMetrics?.delta_port ?? null,
        risksByFactor: detail.result.risks_by_factor,
        attributeRiskMetrics: detail.attribute_risk_metrics,
        constraintEvaluation: detail.constraintEvaluation ?? null,
        resultIssuedAt: detail.receipt?.issued_at ?? null,
        solverStatus: detail.solver_status ?? null,
        displayedExpectedReturnPolicy: detail.expected_return_policy ?? null,
        constraintSummary: detail.result.constraint_summary ?? {},
    };
}

// A result is independent of the latest attempt. Starting or failing a rerun
// does not change this captured projection; the owner decides when to replace it.
export function optimizerResultReducer(state, event) {
    switch (event.type) {
        case 'run-receipt-published':
            return {
                ...state,
                ...event.projection,
                optimizedGmvsMap: event.allocations,
                receiptBaselineGmvsMap: event.baselineGmvs,
                receiptDisplaySource: 'run',
                displayedResultIdentity: event.inputIdentity,
                receiptInputRows: event.pinnedRows,
                receiptPosSizingContextDigest: event.pinnedPosSizingDigest,
                completedOptimizerRun: event.completedRun,
                showPortfolioMetrics: true,
            };
        case 'saved-result-accepted':
            // Saved allocations wait for the requested baseline's rows. Accept
            // the metrics now, preserving that separate installation boundary.
            return { ...state, ...event.projection, showPortfolioMetrics: true };
        case 'saved-overlay-installed':
            return {
                ...state,
                optimizedGmvsMap: event.allocations,
                receiptBaselineGmvsMap: event.baselineGmvs,
                receiptDisplaySource: 'saved',
                displayedResultIdentity: null,
                receiptInputRows: event.pinnedRows,
                receiptPosSizingContextDigest: null,
            };
        case 'saved-identity-bound':
            return state.receiptDisplaySource === 'saved' && state.displayedResultIdentity == null
                ? { ...state, displayedResultIdentity: event.identity }
                : state;
        case 'overlay-cleared':
            // Clearing the projection alone does not detach its completed-run
            // metadata or reset the latest constraint summary.
            return { ...state, ...EMPTY_METRICS, ...EMPTY_OVERLAY };
        case 'result-detached':
            return {
                ...EMPTY_METRICS,
                ...EMPTY_OVERLAY,
                constraintSummary: {},
                completedOptimizerRun: null,
            };
        case 'save-eligibility-revoked':
            return { ...state, completedOptimizerRun: null };
        case 'metrics-visibility-changed':
            return { ...state, showPortfolioMetrics: event.visible };
        default:
            throw new Error(`Unknown Optimizer result transition: ${event.type}`);
    }
}

export default function useOptimizerResultState(scopeKey = '') {
    const scopeRef = useRef({ key: scopeKey });
    if (scopeRef.current.key !== scopeKey) scopeRef.current = { key: scopeKey };
    const owner = scopeRef.current;
    const [state, dispatch] = useReducer(optimizerResultReducer, undefined, createOptimizerResultState);
    // Read-only for the owner. Save acknowledgements must revoke eligibility
    // before React renders, so old async callbacks cannot dispatch another save.
    const acceptedStateRef = useRef(state);
    const send = useCallback((event) => {
        acceptedStateRef.current = optimizerResultReducer(acceptedStateRef.current, event);
        dispatch(event);
    }, []);
    const actions = useMemo(() => ({
        publishRunReceipt({
            receiptResponse,
            runInputRows,
            runInputIdentity,
            runGeneration,
            posSizingContextDigest = null,
            source = null,
        }) {
            const receipt = receiptResponse.receipt;
            send({
                type: 'run-receipt-published',
                projection: runProjection(receiptResponse, owner),
                allocations: receiptResponse.allocations,
                baselineGmvs: receiptBaselineGmvs(receiptResponse.securityRows),
                pinnedRows: runInputRows,
                pinnedPosSizingDigest: posSizingContextDigest,
                inputIdentity: runInputIdentity,
                completedRun: {
                    generation: runGeneration,
                    inputIdentity: runInputIdentity,
                    receiptId: receipt.receipt_id,
                    runOperationId: receipt.run_operation_id,
                    receiptContract: receiptResponse.contract,
                    source,
                },
            });
        },
        acceptSavedResult(detail) {
            send({ type: 'saved-result-accepted', projection: savedProjection(detail, owner) });
        },
        installSavedOverlay(allocations, securityRows, pinnedRows) {
            send({
                type: 'saved-overlay-installed',
                allocations,
                baselineGmvs: receiptBaselineGmvs(securityRows),
                pinnedRows: pinnedRows.map((row) => ({ ...row })),
            });
        },
        bindSavedIdentity(identity) {
            send({ type: 'saved-identity-bound', identity });
        },
        clearOverlay() {
            send({ type: 'overlay-cleared' });
        },
        detachResult() {
            send({ type: 'result-detached' });
        },
        revokeSaveEligibility() {
            send({ type: 'save-eligibility-revoked' });
        },
        setShowPortfolioMetrics(valueOrUpdater) {
            const visible = typeof valueOrUpdater === 'function'
                ? valueOrUpdater(acceptedStateRef.current.showPortfolioMetrics)
                : valueOrUpdater;
            send({ type: 'metrics-visibility-changed', visible });
        },
    }), [owner, send]);

    const resultLoaded = state.factorSliceOwner === owner && Boolean(state.receiptDisplaySource);
    return { state, actions, acceptedStateRef,
        loadedFactorContext: resultLoaded ? state.loadedFactorContext : null,
        loadedFactorResult: resultLoaded };
}
